import axios from '@fs/zion-axios'
import { isPrivateChatRoom } from '../../global/build-jid'
import { Chat } from '../../global/chatSettingsConfig'
import extractCisId from '../../global/extract-cisid'

const serviceBaseUrl = '/service/rootstech/rtc2021/orchestration'

const ensureValidJid = (jid) => {
  return !jid.includes(Chat.Settings.MUC_DOMAIN) ? `${jid}@${Chat.Settings.MUC_DOMAIN}` : jid
}

export const getBookmarkDetailsByBulk = ({ jids }) => {
  if (jids?.length > 0) {
    return axios.post(`${serviceBaseUrl}/bookmarks/details`, { ids: jids })
  }
  return Promise.resolve()
}

export const getBookmarks = async () => {
  // Fetch the bookmarks
  const response = await axios.get(`${serviceBaseUrl}/guests/CURRENT/bookmarks`)
  const bookmarksData = response?.data?.bookmarks || []

  // Fetch the description for the chat rooms
  const jids = bookmarksData.map((room) => room.jid)

  const details = await getBookmarkDetailsByBulk({ jids })
  const detailsData = details?.data?.bookmarks || {}

  // Merge the data together
  const newData = bookmarksData.map((mark) => {
    const matchingDetails = detailsData[mark.jid]

    // Append bookmark chat room description.
    // NOTE: Adding guestCount since I have it in this request, but it will be stale for public chat rooms as time goes on.
    return {
      ...mark,
      description: matchingDetails?.description,
      guestCount: matchingDetails?.guestCount,
    }
  })

  return newData
}

export const addBookmark = ({ jid, name, autoJoin = true }) => {
  const data = { jid: ensureValidJid(jid), name, autoJoin }
  return axios.post(`${serviceBaseUrl}/guests/CURRENT/bookmarks`, data)
}

// RTC-955: Sending bookmarks to other people for private group chats.
export const inviteOthersToBookmark = ({ jid, name, toJidList }) => {
  if (!isPrivateChatRoom(jid)) {
    return Promise.resolve()
  }

  const allCalls = []
  toJidList.forEach((personJid) => {
    const cisUserId = extractCisId(personJid)
    const data = { jid: ensureValidJid(jid), name, autoJoin: true }
    return axios.post(`${serviceBaseUrl}/guests/${cisUserId}/bookmarks`, data)
  })

  return Promise.all(allCalls)
}

export const getBookmarkByJid = ({ jid }) => {
  return getBookmarkDetailsByBulk({ jids: [jid] })
}

export const removeBookmark = ({ jid }) => {
  return axios.delete(`${serviceBaseUrl}/guests/CURRENT/bookmarks/${ensureValidJid(jid)}`)
}

export const getParticipantsWithBookmark = ({ jid }) => {
  return axios.get(`${serviceBaseUrl}/guests/CURRENT/bookmarks/${ensureValidJid(jid)}/participants`)
}

export const getParticipantsCountWithBookmark = ({ jid }) => {
  return axios.get(`${serviceBaseUrl}/guests/CURRENT/bookmarks/${ensureValidJid(jid)}/participants/details`)
}

export const getTopChatRooms = async () => {
  const count = 100

  // Fetch the popular chat rooms
  const response = await axios.get(`${serviceBaseUrl}/bookmarks/search?top=${count}`)
  const topData = response?.data?.results || []

  // Fetch the description and guestCount for these
  const jids = topData.map((room) => room.jid)
  const details = await getBookmarkDetailsByBulk({ jids })
  const detailsData = details?.data?.bookmarks || {}

  // Merge the data together
  const newData = topData.map((room) => {
    const matchingDetails = detailsData[room.jid]
    // Update structure so we can re-use this object in ChatRoomSearchResults.
    return {
      jid: room.jid,
      name: room.name,
      title: room.name,
      guestCount: room.guestCount,
      description: matchingDetails?.description,
    }
  })
  return newData
}
